import { useState, useRef, useEffect, useCallback } from "react";
import { useSettingsStore } from "../hooks/useSettingsStore";
import { useIsMobile } from "../hooks/useIsMobile";

const LICENSE_TYPES = [
  { value: "agent",             label: "סוכן ביטוח" },
  { value: "insurance_agency",  label: "סוכנות ביטוח" },
  { value: "pension_marketer",  label: "משווק פנסיוני" },
  { value: "pension_advisor",   label: "יועץ פנסיוני / תאגיד יועץ" },
];

const EMAIL_PROVIDERS = [
  { value: "gmail",   label: "Gmail",   icon: "🔵" },
  { value: "outlook", label: "Outlook", icon: "🟦" },
  { value: "yahoo",   label: "Yahoo",   icon: "🟣" },
];

const SECTIONS = [
  { key: "user",    label: "⚙️ הגדרות משתמש" },
  { key: "license", label: "🪪 בעל רישיון" },
];

// ── Signature Canvas ─────────────────────────────────────────────────────────
function SignatureCanvas({ saved, onSave }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasDraw, setHasDraw] = useState(false);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const move = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#1e3a5f";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDraw(true);
  }, []);

  const end = useCallback(() => { drawing.current = false; }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    canvasRef.current.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasDraw(false);
  };

  const save = () => {
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave(dataUrl);
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
        צייר את חתימתך בתוך המסגרת:
      </div>
      <canvas
        ref={canvasRef}
        width={380}
        height={140}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{
          border: "2px dashed #cbd5e1", borderRadius: 8, cursor: "crosshair",
          display: "block", background: "#f8fafc", touchAction: "none",
          maxWidth: "100%",
        }}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button onClick={clear} style={outlineBtn}>🗑️ נקה</button>
        <button onClick={save} disabled={!hasDraw} style={{ ...solidBtn, opacity: hasDraw ? 1 : 0.5 }}>💾 שמור חתימה</button>
      </div>
      {saved && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>חתימה שמורה:</div>
          <img src={saved} alt="חתימה" style={{ border: "1px solid #e2e8f0", borderRadius: 6, background: "white", maxWidth: 240 }} />
        </div>
      )}
    </div>
  );
}

// ── Field row ────────────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 14 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
        {label}{required && <span style={{ color: "#dc2626", marginRight: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, type = "text", placeholder = "", dir }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 8,
        fontSize: 13, outline: "none", direction: dir || (type === "email" ? "ltr" : "rtl"),
        textAlign: type === "email" ? "left" : "right", background: "white",
        width: "100%", boxSizing: "border-box",
      }}
    />
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 8,
        fontSize: 13, outline: "none", direction: "rtl", background: "white",
        width: "100%", boxSizing: "border-box", cursor: "pointer",
      }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ── Sub-sections ─────────────────────────────────────────────────────────────
function SectionBox({ title, children }) {
  return (
    <div style={{ background: "white", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 20 }}>
      <div style={{
        padding: "12px 20px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
        borderRadius: "12px 12px 0 0", fontSize: 13, fontWeight: 800, color: "#334155",
      }}>{title}</div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

function UserSettings({ draft, setDraft }) {
  return (
    <>
      <SectionBox title="פרטי יצירת קשר">
        <Field label="מייל ליצירת קשר">
          <TextInput type="email" value={draft.contactEmail} onChange={v => setDraft(d => ({ ...d, contactEmail: v }))} placeholder="you@example.com" />
        </Field>
        <Field label="טלפון">
          <TextInput value={draft.contactPhone} onChange={v => setDraft(d => ({ ...d, contactPhone: v }))} placeholder="050-0000000" />
        </Field>
      </SectionBox>

      <SectionBox title="סוג מייל לשליחת דוחות">
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
          כשתלחץ "שלח למייל" מתוך תמונת לקוח, המערכת תפתח את חלון ניסוח המייל בספק שבחרת.
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {EMAIL_PROVIDERS.map(p => (
            <button
              key={p.value}
              onClick={() => setDraft(d => ({ ...d, emailProvider: p.value }))}
              style={{
                padding: "10px 20px", borderRadius: 10, cursor: "pointer",
                border: draft.emailProvider === p.value ? "2px solid #4338ca" : "2px solid #e2e8f0",
                background: draft.emailProvider === p.value ? "#ede9fe" : "white",
                color: draft.emailProvider === p.value ? "#4338ca" : "#475569",
                fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 8,
              }}
            >
              {p.icon} {p.label}
            </button>
          ))}
        </div>
      </SectionBox>
    </>
  );
}

function LicenseSettings({ draft, setDraft }) {
  const photoInputRef = useRef(null);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setDraft(d => ({ ...d, licensePhotoDataUrl: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <>
      <SectionBox title="מידע כללי">
        <Field label="מספר רישיון / תאגיד" required>
          <TextInput value={draft.licenseNumber} onChange={v => setDraft(d => ({ ...d, licenseNumber: v }))} placeholder="123456" />
        </Field>
        <Field label="סוג רישיון">
          <SelectInput value={draft.licenseType} onChange={v => setDraft(d => ({ ...d, licenseType: v }))} options={LICENSE_TYPES} />
        </Field>
        <Field label="שם בעל רישיון">
          <TextInput value={draft.licenseName} onChange={v => setDraft(d => ({ ...d, licenseName: v }))} placeholder="ישראל ישראלי" />
        </Field>
        <Field label="ת.ז / ח.פ / ח.צ">
          <TextInput value={draft.licenseId} onChange={v => setDraft(d => ({ ...d, licenseId: v }))} placeholder="123456789" />
        </Field>
      </SectionBox>

      <SectionBox title="פרטי קשר">
        <Field label="סלולרי">
          <TextInput value={draft.licenseMobile} onChange={v => setDraft(d => ({ ...d, licenseMobile: v }))} placeholder="050-0000000" />
        </Field>
        <Field label="טלפון נוסף">
          <TextInput value={draft.licensePhone} onChange={v => setDraft(d => ({ ...d, licensePhone: v }))} placeholder="03-0000000" />
        </Field>
        <Field label="מייל">
          <TextInput type="email" value={draft.licenseEmail} onChange={v => setDraft(d => ({ ...d, licenseEmail: v }))} placeholder="you@example.com" />
        </Field>
      </SectionBox>

      <SectionBox title="פרטי איש קשר תפעול">
        <Field label="שם איש קשר">
          <TextInput value={draft.opContactName} onChange={v => setDraft(d => ({ ...d, opContactName: v }))} placeholder="שם מלא" />
        </Field>
        <Field label="טלפון איש קשר">
          <TextInput value={draft.opContactPhone} onChange={v => setDraft(d => ({ ...d, opContactPhone: v }))} placeholder="050-0000000" />
        </Field>
        <Field label="מייל איש קשר">
          <TextInput type="email" value={draft.opContactEmail} onChange={v => setDraft(d => ({ ...d, opContactEmail: v }))} placeholder="ops@example.com" />
        </Field>
      </SectionBox>

      <SectionBox title="חתימה גרפית">
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
          החתימה תשמר במערכת ותאפשר הדבקה לטפסים, ייפויי כוח ומסמכים.
        </div>
        <SignatureCanvas
          saved={draft.signatureDataUrl}
          onSave={url => setDraft(d => ({ ...d, signatureDataUrl: url }))}
        />
        {draft.signatureDataUrl && (
          <button
            onClick={() => setDraft(d => ({ ...d, signatureDataUrl: "" }))}
            style={{ ...outlineBtn, marginTop: 6, fontSize: 11, color: "#dc2626", borderColor: "#fca5a5" }}
          >🗑️ מחק חתימה שמורה</button>
        )}
      </SectionBox>

      <SectionBox title="צילום רישיון">
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
          יש להעלות צילום רישיון בתוקף לשנה הנוכחית על מנת לאפשר הזמנת מסמכי פוליסה בהתאם לחוזר צירוף לביטוח.
        </div>
        {draft.licensePhotoDataUrl ? (
          <div>
            <img
              src={draft.licensePhotoDataUrl}
              alt="צילום רישיון"
              style={{ maxWidth: 260, maxHeight: 180, borderRadius: 8, border: "1px solid #e2e8f0", display: "block", marginBottom: 8 }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => photoInputRef.current?.click()} style={outlineBtn}>🔄 החלף תמונה</button>
              <button onClick={() => setDraft(d => ({ ...d, licensePhotoDataUrl: "" }))} style={{ ...outlineBtn, color: "#dc2626", borderColor: "#fca5a5" }}>🗑️ מחק</button>
            </div>
          </div>
        ) : (
          <button onClick={() => photoInputRef.current?.click()} style={solidBtn}>📷 העלה צילום רישיון</button>
        )}
        <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handlePhoto} />
      </SectionBox>
    </>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function SettingsScreen({ onBack }) {
  const { settings, saveSettings } = useSettingsStore();
  const isMobile = useIsMobile();
  const [activeSection, setActiveSection] = useState("user");
  const [draft, setDraft] = useState(() => ({ ...settings }));
  const [saved, setSaved] = useState(false);

  useEffect(() => { setDraft({ ...settings }); }, [settings]);

  const handleSave = () => {
    saveSettings(draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div style={{
      flex: 1, overflowY: "auto", background: "#f1f5f9",
      direction: "rtl", fontFamily: "'Segoe UI', Arial, sans-serif",
    }}>
      {/* nav bar */}
      <div style={{
        display: "flex",
        flexDirection: isMobile ? "row" : "row",
        background: "white",
        borderBottom: "1px solid #e2e8f0",
        padding: isMobile ? "0 8px" : "0 24px",
        gap: 4,
        overflowX: "auto",
      }}>
        {SECTIONS.map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            style={{
              padding: isMobile ? "10px 14px" : "12px 20px",
              background: "none", border: "none",
              borderBottom: activeSection === s.key ? "3px solid #4338ca" : "3px solid transparent",
              color: activeSection === s.key ? "#4338ca" : "#64748b",
              fontWeight: activeSection === s.key ? 800 : 600,
              fontSize: isMobile ? 13 : 14, cursor: "pointer",
              whiteSpace: "nowrap", transition: "all 0.15s",
            }}
          >{s.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={onBack} style={{ ...outlineBtn, alignSelf: "center", margin: "6px 0", padding: isMobile ? "6px 10px" : "8px 16px", fontSize: isMobile ? 11 : 12 }}>
          ← {isMobile ? "חזור" : "חזרה למסך הבית"}
        </button>
      </div>

      {/* content */}
      <div style={{ maxWidth: 700, margin: "0 auto", padding: isMobile ? "16px 12px" : "24px 24px 60px" }}>
        {activeSection === "user"    && <UserSettings    draft={draft} setDraft={setDraft} />}
        {activeSection === "license" && <LicenseSettings draft={draft} setDraft={setDraft} />}

        {/* save bar */}
        <div style={{
          position: "sticky", bottom: 0,
          background: "white", borderTop: "1px solid #e2e8f0",
          padding: "12px 0", display: "flex", alignItems: "center", gap: 12,
        }}>
          <button onClick={handleSave} style={solidBtn}>💾 שמור הגדרות</button>
          {saved && <span style={{ fontSize: 13, color: "#059669", fontWeight: 700 }}>✅ נשמר בהצלחה</span>}
        </div>
      </div>
    </div>
  );
}

// ── Shared button styles ─────────────────────────────────────────────────────
const solidBtn = {
  padding: "9px 22px", background: "#4338ca", color: "white",
  border: "none", borderRadius: 8, cursor: "pointer",
  fontWeight: 700, fontSize: 13, whiteSpace: "nowrap",
};

const outlineBtn = {
  padding: "8px 16px", background: "white", color: "#475569",
  border: "1px solid #cbd5e1", borderRadius: 8, cursor: "pointer",
  fontWeight: 600, fontSize: 12, whiteSpace: "nowrap",
};
